// Utilidades para OrderForm
// Funciones puras extraídas de OrderForm.tsx para mejor organización y reutilización

import React from 'react';
import type { LineItem, PagoParcial, OrderData, Departamento, Ciudad } from '../types/orderFormTypes';
import { BOLD_PARTIAL_MIN_AMOUNT_COP } from '../constants/boldPayments';
import {
  getProductDetailDisplayStock,
  type ComputeProductDisplayStockOptions
} from './productStockDisplayUtils';
import { isClienteNetoActive } from './clienteNetoOrder';

/**
 * Verifica si una ciudad es metropolitana basándose en su código
 */
export const esCiudadMetropolitana = (ciudad: string): boolean => {
  if (!ciudad) return false;
  // Códigos de ciudades metropolitanas
  const codigosMetropolitanos = ['05001', '05088', '05266', '05360', '05631'];
  return codigosMetropolitanos.includes(ciudad);
};

function parseIvaRateValue(raw: unknown, fallback = 0.19): number {
  if (raw === undefined || raw === null || raw === '') return fallback
  const n = Number(raw)
  if (Number.isNaN(n)) return fallback
  return n > 1 ? n / 100 : n
}

/** IVA guardado en la línea del pedido (visualizar: no usar catálogo /multiple). */
export function getLineItemIvaRateFromOrderLine(item: LineItem): number {
  return parseIvaRateValue(item.iva)
}

/** Tasa de IVA como decimal (p. ej. 0.19). Coherente con fila de tabla: (ivaProduct||0)/100 si ivaProduct > 1 */
export function getLineItemIvaRateFromDetails(
  item: LineItem,
  productDetails: { [key: string]: any },
  useOrderLineIvaOnly = false
): number {
  if (useOrderLineIvaOnly) {
    return getLineItemIvaRateFromOrderLine(item)
  }
  const productDetail = productDetails[item.sku || ''] || productDetails[item.product_id] || {}
  const ivaProduct =
    productDetail.iva_ofima ?? productDetail.tax_rate ?? productDetail.iva ?? item.iva
  return parseIvaRateValue(ivaProduct)
}

/**
 * Al activar cliente neto: embebe IVA en el precio unitario (el total de línea no cambia).
 */
export function applyClienteNetoPrecios(
  items: LineItem[],
  productDetails: { [key: string]: any }
): LineItem[] {
  return items.map(item => {
    const t = getLineItemIvaRateFromDetails(item, productDetails)
    const P = parseFloat(String(item.price)) || 0
    const Q = item.quantity || 1
    const D = Number(item.discount) || 0
    const subtotalConDescuento = P * Q - D
    const iva = subtotalConDescuento * t
    const oldTotal = subtotalConDescuento + iva
    const newP = Q > 0 ? (oldTotal + D) / Q : P
    return { ...item, price: String(Math.round(newP * 100) / 100) }
  })
}

/**
 * Al desactivar cliente neto: quita IVA del precio unitario (inverso de applyClienteNetoPrecios).
 * No usa catálogo externo; mantiene el mismo total de línea.
 */
export function stripClienteNetoPrecios(
  items: LineItem[],
  productDetails: { [key: string]: any }
): LineItem[] {
  return items.map(item => {
    const t = getLineItemIvaRateFromDetails(item, productDetails)
    const P = parseFloat(String(item.price)) || 0
    const Q = item.quantity || 1
    const D = Number(item.discount) || 0
    const grossLine = P * Q - D
    if (grossLine <= 0) {
      return item
    }
    const netLine = grossLine / (1 + t)
    const newP = Q > 0 ? (netLine + D) / Q : P / (1 + t)
    return { ...item, price: String(Math.round(newP * 100) / 100) }
  })
}

/** Restaura precio unitario desde catálogo (no usar al togglear cliente neto) */
export function restorePreciosCatalogoLineItems(
  items: LineItem[],
  productDetails: { [key: string]: any }
): LineItem[] {
  return items.map(item => {
    const detail = productDetails[item.sku || ''] || productDetails[item.product_id] || {}
    const restore = detail.precio_ofima ?? detail.price
    if (restore == null || restore === '') return item
    return { ...item, price: String(restore) }
  })
}

const IVA_ENVIO_CLIENTE_NETO = 0.19

export const DEFAULT_SHIPPING_IVA_RATE = IVA_ENVIO_CLIENTE_NETO

function roundMoneyAmount(value: number): number {
  return Math.round(value * 100) / 100
}

/**
 * El costo de envío en el formulario es bruto (IVA incluido).
 * En pedidos normales: neto para la fila Envío e IVA sumado a impuestos.
 */
export function splitShippingCostForTotals(
  costoEnvioGross: string | number = '0',
  clienteNeto: boolean = false,
  ivaRate: number = DEFAULT_SHIPPING_IVA_RATE
): { shippingNet: number; shippingTax: number; shippingGross: number } {
  const shippingGross = parseFloat(String(costoEnvioGross ?? '0')) || 0
  if (shippingGross <= 0 || clienteNeto) {
    return { shippingNet: shippingGross, shippingTax: 0, shippingGross }
  }
  const shippingNet = shippingGross / (1 + ivaRate)
  const shippingTax = shippingGross - shippingNet
  return {
    shippingNet: roundMoneyAmount(shippingNet),
    shippingTax: roundMoneyAmount(shippingTax),
    shippingGross
  }
}

/**
 * Valor de envío para `costo_envio` / `shipping_total` en el API.
 * Siempre envía exactamente lo ingresado en el input, con o sin cliente neto.
 */
export function getShippingCostForOrderPayload(
  costoEnvioUserGross: string | number = '0',
  _clienteNeto: boolean = false
): string {
  const gross = parseFloat(String(costoEnvioUserGross ?? '0')) || 0
  if (gross <= 0) return '0'
  return roundMoneyAmount(gross).toFixed(2)
}

/** Total del pedido (líneas + envío bruto) para el campo `total` del API. */
export function getOrderGrandTotalForPayload(
  lineItems: LineItem[] = [],
  productDetails: { [key: string]: unknown } = {},
  costoEnvioUserGross: string | number = '0',
  clienteNeto: boolean = false
): string {
  const totals = calculateTotals(
    lineItems,
    productDetails,
    String(costoEnvioUserGross ?? '0'),
    clienteNeto
  )
  const grand = parseFloat(totals.total) + parseFloat(totals.shipping)
  return roundMoneyAmount(grand).toFixed(2)
}

/** Costo de envío con IVA incluido (cliente neto) */
export function applyClienteNetoCostoEnvio(
  value: string,
  ivaDecimal: number = IVA_ENVIO_CLIENTE_NETO
): string {
  const n = parseFloat(value) || 0
  if (n <= 0) return value
  return String(Math.round(n * (1 + ivaDecimal) * 100) / 100)
}

/** Restaura costo de envío sin IVA al desactivar cliente neto */
export function restoreClienteNetoCostoEnvio(
  value: string,
  ivaDecimal: number = IVA_ENVIO_CLIENTE_NETO
): string {
  const n = parseFloat(value) || 0
  if (n <= 0) return value
  return String(Math.round((n / (1 + ivaDecimal)) * 100) / 100)
}

/**
 * Calcula los totales del pedido (subtotal, descuento, impuestos, envío, total)
 * @param clienteNeto Si es true, no se suman impuestos al total (IVA 0% en totales).
 */
export const calculateTotals = (
  items: LineItem[] = [],
  productDetails: { [key: string]: any } = {},
  costoEnvio: string = '0',
  clienteNeto: boolean = false,
  useOrderLineValues = false
) => {
  const subtotal = items.reduce((sum, item) => {
    return sum + (parseFloat(item.price) * item.quantity);
  }, 0);

  const discount = items.reduce((sum, item) => {
    const d = item.discount !== undefined && item.discount !== null ? Number(item.discount) : 0;
    return sum + (isNaN(d) ? 0 : d);
  }, 0);

  const lineTaxes = clienteNeto
    ? 0
    : items.reduce((sum, item) => {
        const itemSubtotal = parseFloat(item.price) * item.quantity;
        const itemDiscount = item.discount !== undefined && item.discount !== null ? Number(item.discount) : 0;
        const itemIva = getLineItemIvaRateFromDetails(item, productDetails, useOrderLineValues);
        return sum + ((itemSubtotal - itemDiscount) * itemIva);
      }, 0);

  const { shippingNet, shippingTax } = splitShippingCostForTotals(
    costoEnvio,
    clienteNeto
  );
  const taxes = lineTaxes + shippingTax;
  const total = subtotal - discount + taxes;

  return {
    subtotal: subtotal.toFixed(2),
    discount: discount.toFixed(2),
    taxes: taxes.toFixed(2),
    shipping: shippingNet.toFixed(2),
    total: total.toFixed(2)
  };
};

/** Total COP del pedido (líneas + envío) para Bold y validaciones; recalcula desde el formulario actual. */
export function getOrderTotalCopAmount(
  lineItems: LineItem[] | undefined,
  productDetails: { [key: string]: unknown } = {},
  costoEnvio: string | number = '0',
  extraDetails?: OrderData['extraDetails']
): number {
  const totals = calculateTotals(
    lineItems || [],
    productDetails,
    String(costoEnvio ?? '0'),
    isClienteNetoActive(extraDetails)
  )
  const base = parseFloat(totals.total || '0') || 0
  const shipping = parseFloat(totals.shipping || '0') || 0
  return Math.round(base + shipping)
}

/**
 * Genera las opciones de departamentos para un select
 */
export const renderDepartamentoOptions = (
  addressType: 'billing' | 'shipping',
  formData: OrderData,
  deptos: Departamento[]
): JSX.Element[] => {
  const options: JSX.Element[] = [];
  const selectedStateCode = addressType === 'billing'
    ? formData.billingAddress?.state
    : formData.shippingAddress?.state;

  if (!selectedStateCode) {
    options.push(<option key="default" value="">Seleccione</option>);
  }

  deptos.forEach((depto) => {
    const code = depto.Codigo.toString();
    options.push(
      <option key={code} value={code}>
        {depto.Nombre}
      </option>
    );
  });
  
  return options;
};

/**
 * Genera las opciones de ciudades para un select basándose en el departamento seleccionado
 */
export const renderCiudadOptions = (
  addressType: 'billing' | 'shipping',
  formData: OrderData,
  cities: Ciudad[]
): JSX.Element[] => {
  const options: JSX.Element[] = [];
  const selectedStateCode = addressType === 'billing'
    ? formData.billingAddress?.state
    : formData.shippingAddress?.state;

  options.push(<option key="default" value="">Seleccione</option>);

  const codigoDepartamento = selectedStateCode || '';

  cities.forEach((ciudad) => {
    const perteneceAlDepartamento = codigoDepartamento
      ? ciudad.codigo.startsWith(codigoDepartamento)
      : true;
    if (perteneceAlDepartamento) {
      options.push(
        <option key={ciudad.codigo} value={ciudad.codigo}>
          {ciudad.ciudad}
        </option>
      );
    }
  });
  
  return options;
};

/**
 * Combos/kits: el SKU padre puede traer saldo erróneo en OFIMA; solo validan componentes.
 */
export function isLineItemStockSufficient(
  item: LineItem,
  productDetails: { [key: string]: any } = {},
  stockOptions?: ComputeProductDisplayStockOptions
): boolean {
  const getStock = (detail: { [key: string]: any } | undefined) =>
    getProductDetailDisplayStock(detail, stockOptions)

  if (item.is_combo === 1 && item.components?.length) {
    return item.components.every((component) => {
      const componentDetail = productDetails[component.sku]
      const needed = (component.quantity || 1) * item.quantity
      return getStock(componentDetail) >= needed
    })
  }

  const productDetail = productDetails[item.sku || item.product_id]
  return getStock(productDetail) >= item.quantity
}

/** Stock a mostrar en tabla: combos usan el mínimo entre componentes (ignora saldo del padre). */
export function getLineItemDisplayStock(
  item: LineItem,
  productDetails: { [key: string]: any } = {},
  stockOptions?: ComputeProductDisplayStockOptions
): number {
  const getStock = (detail: { [key: string]: any } | undefined) =>
    getProductDetailDisplayStock(detail, stockOptions)

  if (item.is_combo === 1 && item.components?.length) {
    const stocks = item.components.map((component) =>
      getStock(productDetails[component.sku])
    )
    if (stocks.length === 0) return 0
    return Math.min(...stocks)
  }

  return getStock(productDetails[item.sku || item.product_id])
}

/**
 * Verifica si hay stock disponible para todos los productos del pedido
 */
export const isStockAvailable = (
  mode: 'view' | 'edit' | 'create',
  formData: OrderData,
  productDetails: { [key: string]: any } = {},
  stockOptions?: ComputeProductDisplayStockOptions
): boolean => {
  if (mode === 'create') {
    if (!formData.lineItems?.length) return false
    return formData.lineItems.every((item) =>
      isLineItemStockSufficient(item, productDetails, stockOptions)
    )
  }
  if (mode === 'edit') {
    const estadosQueVerificanStock = ['processing', 'pending', 'send_later', 'verify_transaction']
    if (!estadosQueVerificanStock.includes(formData.status || '')) {
      return true
    }
    if (!formData.lineItems?.length) return false
    return formData.lineItems.every((item) =>
      isLineItemStockSufficient(item, productDetails, stockOptions)
    )
  }
  return false
}

/**
 * Valida que los pagos parciales sean válidos y coincidan con el total del pedido
 */
export const isPagosParcialesValid = (
  formData: OrderData,
  pagosParciales: PagoParcial[],
  productDetails: { [key: string]: any } = {},
  costoEnvio: string = '0'
): boolean => {
  const paymentMethod = (formData.extraDetails?.payment_method || '').toString().trim().toLowerCase()
  const isPartPayment = paymentMethod === 'part'
  const isPlanSepare = paymentMethod === 'plan_separe'

  // Si no es pagos parciales, no hay validación especial
  if (!isPartPayment && !isPlanSepare) {
    return true;
  }
  
  // Si no hay pagos parciales definidos aún, no validar
  if (!pagosParciales || pagosParciales.length === 0) {
    return false;
  }
  
  // Verificar que todos los pagos tengan valores válidos
  const pagosValidos = pagosParciales.every(pago => {
    if (!pago) return false;
    
    const hasMetodoPago = pago.metodo_pago && pago.metodo_pago.trim() !== '';
    const valorRounded = Math.round(Number(pago.valor) || 0)
    const hasValor = valorRounded > 0;
    const boldMontoOk =
      String(pago.metodo_pago || '').trim().toLowerCase() !== 'bold' ||
      valorRounded >= BOLD_PARTIAL_MIN_AMOUNT_COP
    const requiereTransactionId =
      pago.metodo_pago === 'wompi' ||
      pago.metodo_pago === 'mpago' ||
      pago.metodo_pago === 'bacs' ||
      (isPlanSepare && pago.metodo_pago === 'addi')
    const hasTransactionId = !requiereTransactionId || (requiereTransactionId && pago.id_transaccion);
    
    return hasMetodoPago && hasValor && hasTransactionId && boldMontoOk;
  });
  
  if (!pagosValidos) {
    return false;
  }
  
  // En Plan Separe no exigimos igualdad de suma; basta con tener al menos un abono válido
  if (isPlanSepare) {
    const totals = calculateTotals(
      formData.lineItems || [],
      productDetails,
      costoEnvio,
      isClienteNetoActive(formData.extraDetails)
    );
    const sumaPagosParciales = pagosParciales.reduce((sum, pago) => {
      return sum + (pago?.valor || 0);
    }, 0);
    const totalPedidoSinRedondear =
      parseFloat(totals.total) + parseFloat(totals.shipping || '0');
    const totalPedido = Math.round(totalPedidoSinRedondear);
    const sumaRedondeada = Math.round(sumaPagosParciales);

    return sumaRedondeada <= totalPedido
  }

  // Para pagos parciales, verificar que la suma coincida con el total
  const totals = calculateTotals(
    formData.lineItems || [],
    productDetails,
    costoEnvio,
    isClienteNetoActive(formData.extraDetails)
  );
  const sumaPagosParciales = pagosParciales.reduce((sum, pago) => {
    return sum + (pago?.valor || 0);
  }, 0);

  const totalPedidoSinRedondear =
    parseFloat(totals.total) + parseFloat(totals.shipping || '0');
  const totalPedido = Math.round(totalPedidoSinRedondear);
  const sumaRedondeada = Math.round(sumaPagosParciales);
  const diferencia = Math.abs(totalPedido - sumaRedondeada);

  return diferencia === 0;
};

